import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Branch } from "@/types/branch";

type OrderMode = "Pick up" | "Delivery" | "Appointment";

interface UseCheckoutFlowProps {
  branch: Branch | undefined;
  hasServiceInCart: boolean;
  merchantSlug: string;
  handleCartClose?: () => void;
}

export function useCheckoutFlow({
  branch,
  hasServiceInCart,
  merchantSlug,
  handleCartClose,
}: UseCheckoutFlowProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCartPage, setShowCartPage] = useState(true);
  const [showUpsellPage, setShowUpsellPage] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [showPostPaidForm, setShowPostPaidForm] = useState(false);
  const [orderMode, setOrderMode] = useState<OrderMode>("Pick up");
  const [state, setState] = useState(searchParams.get("state") || "");

  // Set order mode based on branch delivery feature
  useEffect(() => {
    if (branch?.delivery_feature) {
      setOrderMode("Delivery");
    } else {
      setOrderMode("Pick up");
    }
  }, [branch]);

  // Scroll to payment form when showing payment
  useEffect(() => {
    if (showPayment && state !== "") {
      const scrollToPaymentForm = () => {
        const paymentForm = document.getElementById("payment-form");
        if (paymentForm) {
          paymentForm.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };
      setTimeout(scrollToPaymentForm, 100);
    }
  }, [showPayment, state]);

  const proceedToCheckout = useCallback(
    (openGuestModal: () => void) => {
      setShowCartPage(false);
      if (state === "") {
        openGuestModal();
      } else {
        if (branch?.upsell_feature) {
          setShowUpsellPage(true);
        } else {
          if (orderMode === "Pick up") {
            if (hasServiceInCart) {
              if (branch?.payment_preference === "PostPaid") {
                setShowPostPaidForm(true);
              } else {
                setShowCheckout(true);
              }
            } else {
              setShowCheckout(true);
            }
          } else {
            setShowDeliveryForm(true);
          }
        }
      }
    },
    [state, branch, orderMode, hasServiceInCart],
  );

  const handleContinueCheckout = useCallback(() => {
    if (branch?.payment_preference === "PostPaid") {
      toast.success("Fill out the appointment details to confirm order");
      setShowPostPaidForm(true);
    } else if (hasServiceInCart) {
      setOrderMode("Appointment");
      setShowCheckout(true);
    } else if (orderMode === "Pick up") {
      setShowCheckout(true);
    } else {
      setShowDeliveryForm(true);
    }
    setShowUpsellPage(false);
  }, [branch, hasServiceInCart, orderMode]);

  const navigateToCart = useCallback(
    (onCheckoutClick: () => void) => {
      const currentPath = window.location.pathname;
      if (currentPath !== `${merchantSlug}/cart`) {
        navigate(`${merchantSlug}/cart`);
      }
      handleCartClose?.();
      onCheckoutClick();
    },
    [merchantSlug, navigate, handleCartClose],
  );

  const handleGuestContinue = useCallback(
    (closeGuestModal: () => void) => {
      setState("guest");
      setSearchParams({ state: "guest" });
      closeGuestModal();
      if (branch?.upsell_feature) {
        setShowUpsellPage(true);
      } else if (orderMode === "Delivery") {
        setShowDeliveryForm(true);
      } else {
        if (hasServiceInCart && branch?.payment_preference === "PostPaid") {
          setShowPostPaidForm(true);
        } else {
          setShowCheckout(true);
        }
      }
    },
    [branch, orderMode, hasServiceInCart, setSearchParams],
  );

  const goToLogin = useCallback(
    (closeModal: () => void) => {
      closeModal();
      navigate(`${merchantSlug}/customer-login`);
    },
    [merchantSlug, navigate],
  );

  const goToLoginWithPhone = useCallback(
    (phone: string, closeModal: () => void) => {
      closeModal();
      navigate(`${merchantSlug}/customer-login?phone=${phone}`);
    },
    [merchantSlug, navigate],
  );

  const resetToCart = useCallback(() => {
    setShowPayment(false);
    setShowCheckout(false);
    setShowCartPage(true);
    setShowDeliveryForm(false);
    setShowPostPaidForm(false);
    setShowUpsellPage(false);
  }, []);

  const goToPayment = useCallback(() => {
    setShowCheckout(false);
    setShowPayment(true);
  }, []);

  const goToPostPaid = useCallback(() => {
    setShowCheckout(false);
    setShowPostPaidForm(true);
  }, []);

  const goToCheckoutFromPayment = useCallback(() => {
    setShowPayment(false);
    setShowCheckout(true);
    setShowCartPage(false);
  }, []);

  const goToDeliveryForm = useCallback(() => {
    setShowCheckout(false);
    setShowDeliveryForm(true);
  }, []);

  const onDeliverySuccess = useCallback(() => {
    setShowDeliveryForm(false);
    setShowCheckout(true);
  }, []);

  const setCustomerState = useCallback(
    (customerState: string) => {
      setState(customerState);
      setSearchParams({ state: customerState });
    },
    [setSearchParams],
  );

  // Wrapper to accept string for compatibility with view components
  const updateOrderMode = useCallback((mode: string) => {
    setOrderMode(mode as OrderMode);
  }, []);

  return {
    showCheckout,
    showPayment,
    showCartPage,
    showUpsellPage,
    showDeliveryForm,
    showPostPaidForm,
    orderMode,
    state,
    setShowCheckout,
    setShowPayment,
    setShowCartPage,
    setShowUpsellPage,
    setShowDeliveryForm,
    setShowPostPaidForm,
    setOrderMode: updateOrderMode,
    setState,
    setCustomerState,
    proceedToCheckout,
    handleContinueCheckout,
    navigateToCart,
    handleGuestContinue,
    goToLogin,
    goToLoginWithPhone,
    resetToCart,
    goToPayment,
    goToPostPaid,
    goToCheckoutFromPayment,
    goToDeliveryForm,
    onDeliverySuccess,
  };
}
